//
// The organization's policy rules (spec 17, PolicyController): typed rules of four kinds, each tenant-wide or scoped
// to one Skill. Administrators write them; Security Reviewers read them. Mirrored by numeric value from the API
// (Core/Services/PolicyManager/PolicyRules.cs).
//

export enum PolicyKind {
  WriteApproval = 0,
  ArgumentThreshold = 1,
  ExecutionLimits = 2,
  AdHocExecution = 3,
}

// Who approves a call a WriteApproval or ArgumentThreshold rule stops
export enum ApproverMode {
  Requester = 0,
  Group = 1,
}

// Numbers take all but Contains; text takes Equal, NotEqual and Contains; true and false take Equal and NotEqual
export enum ThresholdComparator {
  GreaterThan = 0,
  GreaterThanOrEqual = 1,
  LessThan = 2,
  LessThanOrEqual = 3,
  Equal = 4,
  NotEqual = 5,
  Contains = 6,
}

// The type of a threshold value, which decides the comparators it takes
export enum ThresholdValueType {
  Number = 0,
  Text = 1,
  Boolean = 2,
}

export type ThresholdValue = number | string | boolean;

// The field lengths and ranges the API accepts (Policy, PolicyManager, ArgumentPath, ExecutionLimitsConfig)
export const policyNameMaxLength = 128;
export const policyFieldPathMaxLength = 256;
export const policyThresholdTextMaxLength = 256;
export const policyCapabilityKeyMaxLength = 64;
export const policyToolNameMaxLength = 128;
export const policyMaxToolCallsLimit = 1000;
export const policyMaxMinutesLimit = 1440;
export const policyMaxTokensLimit = 100000000;

// Dotted names and [index] (amount, body.invoice.total, lines[0].price): the API's ArgumentPath.TryParse
const policyFieldPathSegmentPattern = /^[A-Za-z_$][A-Za-z0-9_$-]*(\[\d{1,5}\])*$/;
const policyFieldPathMaxSegments = 16;
const policyFieldPathMaxIndex = 10000;

export const isValidPolicyFieldPath = (path: string): boolean => {
  if (!path || path.length > policyFieldPathMaxLength || path !== path.trim()) return false;

  const segments = path.split('.');
  if (segments.length > policyFieldPathMaxSegments) return false;

  return segments.every(
    (segment) =>
      policyFieldPathSegmentPattern.test(segment) &&
      [...segment.matchAll(/\[(\d+)\]/g)].every((match) => Number(match[1]) <= policyFieldPathMaxIndex),
  );
};

export const getThresholdValueType = (value: ThresholdValue): ThresholdValueType => {
  if (typeof value === 'number') return ThresholdValueType.Number;
  if (typeof value === 'boolean') return ThresholdValueType.Boolean;

  return ThresholdValueType.Text;
};

export const getComparatorsForValueType = (type: ThresholdValueType): ThresholdComparator[] => {
  switch (type) {
    case ThresholdValueType.Number:
      return [
        ThresholdComparator.GreaterThan,
        ThresholdComparator.GreaterThanOrEqual,
        ThresholdComparator.LessThan,
        ThresholdComparator.LessThanOrEqual,
        ThresholdComparator.Equal,
        ThresholdComparator.NotEqual,
      ];
    case ThresholdValueType.Text:
      return [ThresholdComparator.Equal, ThresholdComparator.NotEqual, ThresholdComparator.Contains];
    default:
      return [ThresholdComparator.Equal, ThresholdComparator.NotEqual];
  }
};

// The symbol the API and skill.yaml use for a comparator
export const getComparatorSymbol = (comparator: ThresholdComparator): string => {
  switch (comparator) {
    case ThresholdComparator.GreaterThan:
      return '>';
    case ThresholdComparator.GreaterThanOrEqual:
      return '>=';
    case ThresholdComparator.LessThan:
      return '<';
    case ThresholdComparator.LessThanOrEqual:
      return '<=';
    case ThresholdComparator.Equal:
      return '==';
    case ThresholdComparator.NotEqual:
      return '!=';
    default:
      return 'contains';
  }
};

// WriteApproval: false covers the writes the capability does not allow on its own; true every write
export class WriteApprovalConfig {
  allWrites: boolean;

  constructor() {
    this.allWrites = false;
  }

  clone(): WriteApprovalConfig {
    const output = new WriteApprovalConfig();
    output.allWrites = this.allWrites;

    return output;
  }

  isEqual(other: WriteApprovalConfig | undefined): boolean {
    return other !== undefined && other.allWrites === this.allWrites;
  }
}

// ArgumentThreshold: the argument at the field path compared with the value, optionally for one capability or tool
export class ArgumentThresholdConfig {
  fieldPath: string;

  comparator: ThresholdComparator;

  value: ThresholdValue;

  // only calls to the capability with this key (case-insensitive); undefined for every capability
  capabilityKey?: string;

  // only calls of this tool (the name the model calls); undefined for every tool
  toolName?: string;

  constructor() {
    this.fieldPath = '';
    this.comparator = ThresholdComparator.GreaterThan;
    this.value = 0;
  }

  clone(): ArgumentThresholdConfig {
    const output = new ArgumentThresholdConfig();
    output.fieldPath = this.fieldPath;
    output.comparator = this.comparator;
    output.value = this.value;
    output.capabilityKey = this.capabilityKey;
    output.toolName = this.toolName;

    return output;
  }

  isEqual(other: ArgumentThresholdConfig | undefined): boolean {
    return (
      other !== undefined &&
      other.fieldPath === this.fieldPath &&
      other.comparator === this.comparator &&
      other.value === this.value &&
      (other.capabilityKey ?? '') === (this.capabilityKey ?? '') &&
      (other.toolName ?? '') === (this.toolName ?? '')
    );
  }
}

// ExecutionLimits: undefined leaves a limit to the tenant-wide policy, skill.yaml and the platform bound
export class ExecutionLimitsConfig {
  maxToolCalls?: number;

  maxMinutes?: number;

  // model tokens, input plus output, of one execution
  maxTokens?: number;

  clone(): ExecutionLimitsConfig {
    const output = new ExecutionLimitsConfig();
    output.maxToolCalls = this.maxToolCalls;
    output.maxMinutes = this.maxMinutes;
    output.maxTokens = this.maxTokens;

    return output;
  }

  isEqual(other: ExecutionLimitsConfig | undefined): boolean {
    return (
      other !== undefined &&
      other.maxToolCalls === this.maxToolCalls &&
      other.maxMinutes === this.maxMinutes &&
      other.maxTokens === this.maxTokens
    );
  }
}

// AdHocExecution: whether a request without an approved Skill may run
export class AdHocExecutionConfig {
  allowed: boolean;

  constructor() {
    this.allowed = true;
  }

  clone(): AdHocExecutionConfig {
    const output = new AdHocExecutionConfig();
    output.allowed = this.allowed;

    return output;
  }

  isEqual(other: AdHocExecutionConfig | undefined): boolean {
    return other !== undefined && other.allowed === this.allowed;
  }
}

// One rule of the policy engine. The configuration of its kind is in the one matching member; the others are undefined.
export class Policy {
  // empty for a built-in rule, which has no row and cannot be written
  policyId: string;

  name: string;

  kind: PolicyKind;

  enabled: boolean;

  // undefined for a tenant-wide rule
  skillId?: string;

  // the scoped Skill's name; undefined for a tenant-wide rule or a Skill that no longer exists
  skillName?: string;

  // meaningful for WriteApproval and ArgumentThreshold only
  approverMode: ApproverMode;

  approverGroupId?: string;

  approverGroupName?: string;

  writeApproval?: WriteApprovalConfig;

  argumentThreshold?: ArgumentThresholdConfig;

  executionLimits?: ExecutionLimitsConfig;

  adHocExecution?: AdHocExecutionConfig;

  // the rule a tenant has while it has no policy of the kind: the default write approval, the ad hoc setting
  isBuiltIn: boolean;

  createdById: string;

  created: Date;

  // the administrator who last wrote the rule
  authorUserId: string;

  modified: Date;

  constructor() {
    this.policyId = '';
    this.name = '';
    this.kind = PolicyKind.WriteApproval;
    this.enabled = true;
    this.approverMode = ApproverMode.Requester;
    this.isBuiltIn = false;
    this.createdById = '';
    this.created = new Date();
    this.authorUserId = '';
    this.modified = new Date();
  }

  // A new rule of the kind with the configuration of that kind filled with its defaults
  static create(kind: PolicyKind): Policy {
    const output = new Policy();
    output.kind = kind;
    switch (kind) {
      case PolicyKind.WriteApproval:
        output.writeApproval = new WriteApprovalConfig();
        break;
      case PolicyKind.ArgumentThreshold:
        output.argumentThreshold = new ArgumentThresholdConfig();
        output.approverMode = ApproverMode.Group;
        break;
      case PolicyKind.ExecutionLimits:
        output.executionLimits = new ExecutionLimitsConfig();
        break;
      default:
        output.adHocExecution = new AdHocExecutionConfig();
        break;
    }

    return output;
  }

  // WriteApproval and ArgumentThreshold name an approver; the other kinds have none
  hasApprover(): boolean {
    return this.kind === PolicyKind.WriteApproval || this.kind === PolicyKind.ArgumentThreshold;
  }

  clone(): Policy {
    const output = new Policy();
    output.policyId = this.policyId;
    output.name = this.name;
    output.kind = this.kind;
    output.enabled = this.enabled;
    output.skillId = this.skillId;
    output.skillName = this.skillName;
    output.approverMode = this.approverMode;
    output.approverGroupId = this.approverGroupId;
    output.approverGroupName = this.approverGroupName;
    output.writeApproval = this.writeApproval?.clone();
    output.argumentThreshold = this.argumentThreshold?.clone();
    output.executionLimits = this.executionLimits?.clone();
    output.adHocExecution = this.adHocExecution?.clone();
    output.isBuiltIn = this.isBuiltIn;
    output.createdById = this.createdById;
    output.created = this.created;
    output.authorUserId = this.authorUserId;
    output.modified = this.modified;

    return output;
  }

  // Compares what PUT policies/{id} writes: name, scope, approver and the configuration of the kind
  isEqual(other: Policy): boolean {
    const isSameConfig = (): boolean => {
      switch (this.kind) {
        case PolicyKind.WriteApproval:
          return this.writeApproval?.isEqual(other.writeApproval) ?? other.writeApproval === undefined;
        case PolicyKind.ArgumentThreshold:
          return this.argumentThreshold?.isEqual(other.argumentThreshold) ?? other.argumentThreshold === undefined;
        case PolicyKind.ExecutionLimits:
          return this.executionLimits?.isEqual(other.executionLimits) ?? other.executionLimits === undefined;
        default:
          return this.adHocExecution?.isEqual(other.adHocExecution) ?? other.adHocExecution === undefined;
      }
    };

    return (
      other.policyId === this.policyId &&
      other.kind === this.kind &&
      other.name === this.name &&
      (other.skillId ?? '') === (this.skillId ?? '') &&
      other.approverMode === this.approverMode &&
      (other.approverGroupId ?? '') === (this.approverGroupId ?? '') &&
      isSameConfig()
    );
  }
}

// One entry of a policy's change log (GET policies/{id}/audit)
export class PolicyAuditEntry {
  auditTrailEntryId: number;

  auditTrailActionId: number;

  // ATA-PO-001 .. ATA-PO-004
  actionCode: string;

  // the action's label in the caller's language
  action: string;

  created: Date;

  userId: string;

  userName: string;

  // Name, Scope, Config, Approver or Enabled
  property: string;

  oldValue: string;

  newValue: string;

  // oldValue and newValue in words (the API's display in the reader's language); empty when the API sent none
  oldDisplay: string;

  newDisplay: string;

  constructor() {
    this.auditTrailEntryId = 0;
    this.auditTrailActionId = 0;
    this.actionCode = '';
    this.action = '';
    this.created = new Date();
    this.userId = '';
    this.userName = '';
    this.property = '';
    this.oldValue = '';
    this.newValue = '';
    this.oldDisplay = '';
    this.newDisplay = '';
  }
}

// A skill.yaml approvals condition as the API compiled it at parse time: the rule the gate enforces for it
export class CompiledApprovalRule {
  // WriteApproval (every write of the Skill) or ArgumentThreshold
  kind: PolicyKind;

  approverMode: ApproverMode;

  approverGroupId?: string;

  argumentThreshold?: ArgumentThresholdConfig;

  constructor() {
    this.kind = PolicyKind.WriteApproval;
    this.approverMode = ApproverMode.Requester;
  }
}
